<template>
  <div>
    <div v-if="loading" class="flex justify-center items-center min-h-[200px]">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-custom-yellow"></div>
    </div>
    <template v-else>
      <div class="relative overflow-x-auto shadow-md sm:rounded-lg mt-5">
        <table class="w-full text-left rtl:text-right text-gray-700">
          <thead>
            <tr class="bg-gray-100">
              <th scope="col" class="px-6 py-2">
                <div class="flex items-center">
                  Serial no.
                </div>
              </th>
              <th scope="col" class="px-6">
                <div class="flex items-center">
                  Blood Group
                </div>
              </th>
              <th scope="col" class="px-6">
                <div class="flex items-center">
                  Blood Product
                </div>
              </th>
              <th scope="col" class="px-6">
                <div class="flex items-center">
                  Volume
                </div>
              </th>
              <th scope="col" class="px-6">
                <div class="flex items-center">
                  Origin
                </div>
              </th>
              <th scope="col" class="px-6">
                <div class="flex items-center">
                  Expiry Date
                </div>
              </th>
              <th scope="col" class="px-6">
                <div class="flex items-center justify-center">
                  Status
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(product) in filteredBloodProducts" :key="product.product_id" class="border-b">
              <td class="px-6 py-2">{{ product.pint_serial }}</td>
              <td class="px-6 py-2">{{ product.blood_group?.description }}</td>
              <td class="px-6 py-2">{{ product.unit_product?.name }}</td>
              <td class="px-6 py-2">{{ product.volume || '0' }} ml</td>
              <td class="px-6 py-2">{{ product.source || product.blood_donation?.origin }}</td>
              <td class="px-6 py-2">{{ new Date(product.expiry_date).toLocaleString() ?? 'N/A' }}</td>
              <td class="px-6 py-2 text-center">
                <span class="p-2 rounded-lg min-w-[100px] text-center inline-block" :class="{
                  'bg-green-50 text-green-500': ['Okay', 'Segregated'].includes(product.status),
                  'bg-red-50 text-red-500': ['Expired'].includes(product.status),
                  'bg-blue-50 text-blue-500': ['Assigned'].includes(product.status),
                  'bg-orange-50 text-orange-500': ['Issued'].includes(product.status),
                  'bg-gray-50 text-gray-500': ['Transfused'].includes(product.status),
                  // 'bg-gray-50 text-gray-500': !['Okay', 'Expired', 'Segregated', 'Transfused', 'Assigned'].includes(product.status)
                }">
                  {{ product.status }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="flex justify-center gap-4 mt-4">
        <button @click="previousPage" :disabled="currentPage === 1"
          class="flex items-center gap-2 px-6 py-3 font-bold text-center text-custom-blue align-middle transition-all rounded-lg select-none hover:bg-custom-blue/10 active:bg-custom-blue/20 disabled:pointer-events-none disabled:opacity-50 disabled:shadow-none"
          type="button">
          <svg class="w-6 h-6" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none"
            viewBox="0 0 24 24">
            <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="m15 19-7-7 7-7" />
          </svg>
          Previous
        </button>

        <div class="flex items-center gap-2">
          <button v-for="page in visiblePages" :key="page" @click="goToPage(page)" :class="{
            'bg-custom-blue text-white': page === currentPage,
            'text-custom-blue': page !== currentPage
          }"
            class="relative h-10 max-h-[40px] w-10 max-w-[40px] select-none rounded-lg text-center align-middle font-medium transition-all hover:bg-custom-blue/10 active:bg-custom-blue/20 disabled:pointer-events-none disabled:opacity-50 disabled:shadow-none"
            type="button">
            <span class="absolute transform -translate-x-1/2 -translate-y-1/2 top-1/2 left-1/2">
              {{ page }}
            </span>
          </button>
        </div>

        <button @click="nextPage" :disabled="currentPage === totalPages"
          class="flex items-center gap-2 px-6 py-3 font-bold text-center text-custom-blue align-middle transition-all rounded-lg select-none hover:bg-custom-blue/10 active:bg-custom-blue/20 disabled:pointer-events-none disabled:opacity-50 disabled:shadow-none"
          type="button">
          Next
          <svg class="w-6 h-6" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none"
            viewBox="0 0 24 24">
            <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="m9 5 7 7-7 7" />
          </svg>
        </button>
      </div>
    </template>
  </div>
</template>

<script>
// import CustomButton from '@/components/CustomButton.vue';
import api from '@/services/api';

export default {
  name: 'LiveCounterTab',
  components: {
    // CustomButton,
  },

  props: {
    searchQuery: {
      type: String,
      default: ''
    },
    dateRange: {
      type: String,
      default: ''
    },
    filters: {
      type: Object,
      default: () => ({
        blood_group: '',
        status: '',
        unit_product: '',
        start_date: '',
        end_date: ''
      })
    }
  },

  data() {
    return {
      currentPage: 1,
      totalPages: 0,
      perPage: 10,
      loading: false,
      isLoading: {},
      donations: [],
      products: [],
      error: null,
      localSearchQuery: '',
    }
  },
  created() {
    this.getBloodProducts();
    this.user = this.$store.getters["auth/user"];
    this.facility_logo = this.user.user_roles[0]?.facility_department?.facility?.logo;
    this.localSearchQuery = this.searchQuery;
  },
  computed: {
    visiblePages() {
      const total = this.totalPages;
      const current = this.currentPage;
      const delta = 2;
      const range = [];
      const rangeWithDots = [];
      let l;

      for (let i = 1; i <= total; i++) {
        if (i === 1 || i === total || (i >= current - delta && i <= current + delta)) {
          range.push(i);
        }
      }

      range.forEach(i => {
        if (l) {
          if (i - l === 2) {
            rangeWithDots.push(l + 1);
          } else if (i - l !== 1) {
            rangeWithDots.push('...');
          }
        }
        rangeWithDots.push(i);
        l = i;
      });

      return rangeWithDots;
    },
    filteredBloodProducts() {
      return this.products;
    }
  },
  watch: {
    filters: {
      deep: true,
      handler() {
        this.currentPage = 1; // Reset to first page when filters change
        this.getBloodProducts();
      }
    },
    searchQuery: {
      handler(newVal) {
        this.localSearchQuery = newVal;
        this.currentPage = 1;
        this.getBloodProducts();
      },
      immediate: true
    },
    localSearchQuery: {
      handler(newVal) {
        this.$emit('update:searchQuery', newVal);
        this.currentPage = 1;
        this.getBloodProducts();
      }
    }
  },
  methods: {
    formatDate(date) {
      if (!date) return 'N/A';
      const parsedDate = new Date(date);
      if (isNaN(parsedDate.getTime())) {
        console.error("Invalid date:", date);
        return null;
      }

      return parsedDate.toISOString().split('T')[0];
    },

    applyFilters() {
      this.currentPage = 1;
      this.getBloodProducts();
    },

    cancelFilters() {
      this.$emit('cancel-filters');
      this.currentPage = 1;
      this.getBloodProducts();
    },

    async getBloodProducts() {
      this.loading = true;
      try {
        const queryParams = {
          blood_group: this.filters.blood_group,
          status: this.filters.status,
          unit_product: this.filters.unit_product,
          start_date: this.filters.start_date,
          end_date: this.filters.end_date,
          search_query: this.localSearchQuery,
          page: this.currentPage,
          per_page: this.perPage
        };

        // Remove empty filter values
        // Object.keys(queryParams).forEach(key => {
        //   if (queryParams[key] === '' || queryParams[key] === null || queryParams[key] === undefined) {
        //     delete queryParams[key];
        //   }
        // });

        const response = await api.fetchReportsProducts(queryParams);
        this.products = response.data || [];
        this.currentPage = response.current_page || 1;
        this.totalPages = response.total_pages || 1;
      } catch (error) {
        console.error("Error fetching products:", error);
        this.error = error.message;
      } finally {
        this.loading = false;
      }
    },

    async handleDownloadBloodUnits() {
      const printWindow = document.createElement('iframe');
      printWindow.style.position = 'absolute';
      printWindow.style.width = '0';
      printWindow.style.height = '0';
      document.body.appendChild(printWindow);


      try {
        // Generate table content from filteredBloodProducts
        const queryParams = {
          filter: this.filters, 
          search_query: this.searchQuery,
          all: true
        };

        const response = await api.fetchReportsProducts(queryParams);
        const blood_products = response.data || response;

        const tableRows = blood_products.map((blood_product, index) => `
      <tr class="border-b text-sm">
        <td class="px-3 py-1">${index + 1}</td>
        <td class="px-3 py-1">${blood_product.pint_serial}</td>
        <td class="px-3 py-1">${blood_product.blood_group?.description || ''}</td>
        <td class="px-3 py-1">${blood_product.unit_product?.name || ''}</td>
        <td class="px-3 py-1">${blood_product.volume || '0'} ml</td>
        <td class="px-3 py-1">${blood_product.source || blood_product.blood_donation?.origin || ''}</td>
        <td class="px-3 py-1">${new Date(blood_product.expiry_date).toLocaleString() || 'N/A'}</td>
        <td class="px-3 py-1 text-center">
          <span>
            ${blood_product.status}
          </span>
        </td>
      </tr>
    `).join('');

        // Prepare print content
        const printContent = `
      <div class="page-container">
        <div class="report-container">
          <div class="logo-container">
              <img src="${this.facility_logo}" alt="Hospital Logo" class="logo">
            </div>
          <br><br>
          <h1 class="text-lg font-bold mb-4">Blood Inventory Report</h1>
          <p class="mb-4">Generated on: ${new Date().toLocaleString()}</p>
          
          <div class="table-container">
            <table class="w-full text-left">
              <thead>
                <tr class="bg-gray-100">
                  <th class="px-6 py-2"></th>
                  <th class="px-6 py-2">Serial no.</th>
                  <th class="px-6">Blood Group</th>
                  <th class="px-6">Blood Product</th>
                  <th class="px-6">Volume</th>
                  <th class="px-6">Origin</th>
                  <th class="px-6">Expiry Date</th>
                  <th class="px-6 text-center">Status</th>
                </tr>
              </thead>
              <tbody>
                ${tableRows}
              </tbody>
            </table>
          </div>

          <div class="signatures mt-8">
            <div class="signature-line">
              <p>Prepared by: _________________</p>
              <p>Date: _________________</p>
            </div>
            <div class="signature-line mt-4">
              <p>Verified by: _________________</p>
              <p>Date: _________________</p>
            </div>
          </div>
        </div>
      </div>
    `;

        // Prepare the full document for printing
        const printDocument = `
      <html>
      <head>
        <title>Blood Units Report</title>
        <style>
          @media print {
            @page {
              size: A4;
              margin: 2cm;
            }
          }
          
          body {
            font-family: Arial, sans-serif;
            line-height: 1.6;
            color: #333;
            margin: 0;
            padding: 0;
          }
          .logo{
            width: 120px;
            float:right
          }

          .page-container {
            padding: 20px;
          }

          .report-container {
            max-width: 100%;
            margin: 0 auto;
          }

          table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 20px;
            font-size: 10.5px;
          }

          th, td {
            border: 1px solid #ddd;
            padding: 8px;
            text-align: left;
          }

          th {
            background-color: #F3F4F6;
            font-weight: bold;
          }

          .signatures {
            margin-top: 40px;
            page-break-inside: avoid;
            font-size: 10.5px;
          }

          .signature-line {
            margin-bottom: 20px;
          }

          @media print {
            .status-badge {
              border: 1px solid #ddd;
            }
          }
        </style>
      </head>
      <body>
        ${printContent}
      </body>
      </html>
    `;

        // Open the print document
        printWindow.contentDocument.open();
        printWindow.contentDocument.write(printDocument);
        printWindow.contentDocument.close();

        // Print when the document is loaded
        printWindow.contentWindow.onload = function () {
          printWindow.contentWindow.print();
          // Remove the iframe after printing
          setTimeout(() => {
            document.body.removeChild(printWindow);
          }, 1000);
        };
      } catch (error) {
        console.error('Error generating report:', error);
      }
    },

    // Helper method for status classes
    getStatusClass(status) {
      if (['Okay', 'Segregated'].includes(status)) return 'status-okay';
      if (['Expired'].includes(status)) return 'status-expired';
      return 'status-other';
    },

    goToPage(page) {
      if (page !== '...' && page !== this.currentPage) {
        this.currentPage = page;
        this.getBloodProducts();
      }
    },

    previousPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
        this.getBloodProducts();
      }
    },

    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
        this.getBloodProducts();
      }
    },
  },
}
</script>

<style lang="css" scoped></style>